/**
 * Countries an address question can take: ISO 3166-1 alpha-2 codes. The server's
 * twin is common `countries.py`; `docs/spec/countries-v1.json` is the list both
 * tests compare with. Names are not kept here: the browser writes them in the
 * respondent's language (`countryName`, `Intl.DisplayNames`).
 */

export const DEFAULT_COUNTRY = 'NL';
/** At most this many allowed countries on one question. */
export const COUNTRIES_MAX = 30;

export const COUNTRY_CODES: ReadonlyArray<string> = [
    'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI',
    'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
    'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK',
    'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
    'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
    'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
    'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP',
    'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
    'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF',
    'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
    'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW'
];

const KNOWN = new Set<string>(COUNTRY_CODES);

/** Is `value` one of the ISO 3166-1 alpha-2 codes above (upper case)? */
export const isCountryCode = (value: unknown): value is string => typeof value === 'string' && KNOWN.has(value);

/** A country's name in `language` ("Nederland", "Belgium"); the code itself where the browser has no name. */
export function countryName(code: string, language: string): string {
    try {
        const names = new Intl.DisplayNames([language], { type: 'region' });
        return names.of(code) ?? code;
    } catch {
        return code;
    }
}

/** The countries a question allows (its list, else the Netherlands only). */
export const questionCountries = (field: { properties?: { countries?: string[] } } | undefined | null): string[] => {
    const listed = field?.properties?.countries;
    return Array.isArray(listed) && listed.length ? listed.filter(isCountryCode) : [DEFAULT_COUNTRY];
};
